// hooks/useServiceStorage.ts
import { useState, useEffect } from 'react';
import { ServiceTypeSelectorOption } from '../types/order';

const useServiceStorage = (
  key: string,
  initialValue: ServiceTypeSelectorOption
) => {
  const [service, setService] = useState<ServiceTypeSelectorOption>(() => {
    // Server-side rendering check
    if (typeof window === 'undefined') return initialValue;
    
    try {
      const stored = sessionStorage.getItem(key);
      // Validate the stored value is a valid ServiceTypeSelectorOption
      if (stored && Object.values(ServiceTypeSelectorOption).includes(stored as ServiceTypeSelectorOption)) {
        return stored as ServiceTypeSelectorOption;
      }
      return initialValue;
    } catch (error) {
      console.error('SessionStorage access error:', error);
      return initialValue;
    }
  });

  // Update both state and sessionStorage
  const updateService = (newService: ServiceTypeSelectorOption) => {
    try {
      setService(newService);
      sessionStorage.setItem(key, newService);
    } catch (error) {
      console.error('Failed to update service:', error);
    }
  };

  // Clear from both state and sessionStorage
  const clearService = () => {
    try {
      setService(initialValue);
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear service:', error);
    }
  };

  // Sync between browser tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== service) {
        const newValue = e.newValue as ServiceTypeSelectorOption;
        if (Object.values(ServiceTypeSelectorOption).includes(newValue)) {
          setService(newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, service]);

  return { 
    currentService: service, 
    updateService, 
    clearService,
    isCurrentService: (type: ServiceTypeSelectorOption) => service === type
  };
};

export default useServiceStorage;